/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { AuthService } from "./auth.service";

const API_URL = environment.api;

export type NameValue = { value: number; name: string | undefined };

export type SortDirection = "asc" | "desc";

export type SortOption = { dir: SortDirection; prop: string };

export function buildPageParams(
    offset: number,
    limit: number,
    sorts: SortOption[] = [],
    filter: string | null = null
): Record<string, unknown> {
    const params: Record<string, unknown> = {};

    if (limit !== -1) {
        params["offset"] = offset * limit;
        params["limit"] = limit;
    } else {
        params["offset"] = offset;
        params["limit"] = limit;
    }

    if (sorts.length > 0) {
        params["sort"] =
            sorts[0].dir === "desc" ? `-${sorts[0].prop}` : sorts[0].prop;
    }

    // Needed because otherwise NULL will get serialized as the string "null" in queries
    if (filter) {
        params["filter"] = filter;
    }

    return params;
}

export abstract class HttpService {
    constructor(
        private serviceName: string,
        protected http: HttpClient,
        protected authService: AuthService
    ) {}

    protected get<T>(urlFragment: string, params: any = {}): Observable<T> {
        const url = this.buildUrl(urlFragment);
        return this.http.get<T>(url, { headers: this.createHeaders(), params });
    }

    protected post<T>(urlFragment: string, body: any = {}): Observable<T> {
        const url = this.buildUrl(urlFragment);
        return this.http.post<T>(url, body, { headers: this.createHeaders() });
    }

    protected put<T>(urlFragment: string, body: any = {}): Observable<T> {
        const url = this.buildUrl(urlFragment);
        return this.http.put<T>(url, body, { headers: this.createHeaders() });
    }

    protected delete(urlFragment: string) {
        const url = this.buildUrl(urlFragment);
        return this.http.delete(url, { headers: this.createHeaders() });
    }

    protected postBlob(urlFragment: string, body = {}, params = {}) {
        const url = this.buildUrl(urlFragment);
        const options = {
            headers: this.createHeaders(),
            params,
            responseType: "blob" as "json"
        };

        return this.http.post<Blob>(url, body, options);
    }

    protected get isImpersonating(): boolean {
        return window.localStorage.getItem("original_access_token") ?
                true
            :   false;
    }

    protected createHeaders(): HttpHeaders {
        const h = new HttpHeaders({
            Authorization: `bearer ${this.authService.accessToken}`,
            "Content-Type": "application/json"
        });

        return h;
    }

    private buildUrl(urlFragment: string) {
        return `${API_URL}/api/v1/${urlFragment}`;
    }
}
